import React, { useRef, useState } from "react";
import { FormControl, Typography, FormHelperText, Box, LinearProgress } from "@mui/material";
import axios from 'axios';
import OutlinedInput from "@mui/material/OutlinedInput";
import { getStoredToken } from "../../api";
import Swal from "sweetalert2";

const isDataUrl = (v) => v && typeof v === "string" && v.startsWith("data:");
const isServerUrl = (v) =>
  v &&
  typeof v === "string" &&
  (v.startsWith("http://") || v.startsWith("https://"));
const MAX_VIDEO_SIZE_MB = 1024;
const MAX_PHOTO_SIZE_MB = 50;
const VERCEL_PAYLOAD_LIMIT_MB = 50;
/**
 * Reusable Upload component: URL or file upload for image/video.
 * - Paste URL in the input, or choose a file to upload (stored as data URL in browser).
 *
 * @param {string} type - 'image' | 'video'
 * @param {string} label
 * @param {string} value - current URL or data URL
 * @param {string} fileName - display name when file is uploaded
 * @param {function} onChange - (value: string, fileName?: string) => void
 * @param {string} placeholder - for URL input
 */
const Upload = ({
  type = "image",
  label = "URL or upload",
  value = "",
  fileName = "",
  onChange,
  placeholder = "Paste URL (https://...)",
  uploadEndpoint = "",
  id,
  required = false,
  error = "",
  onBlur,
  ...rest
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef(null);
  const isUploaded = isDataUrl(value) || (fileName && isServerUrl(value));
  const accept = type === "image" ? "image/*" : "video/*";
  const labelText = typeof label === "string" ? label : "upload";
  const inputId = id || `upload-${type}-${labelText.replace(/\s/g, "-")}`;

  const handleUrlChange = (e) => {
    onChange(e.target.value, "");
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isLocal =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname.startsWith("192.168.") ||
      window.location.hostname.endsWith(".local");
    const maxLimitMb = type === "video" ? MAX_VIDEO_SIZE_MB : MAX_PHOTO_SIZE_MB;
    const limitMb =
      uploadEndpoint && !isLocal ? VERCEL_PAYLOAD_LIMIT_MB : maxLimitMb;
    const maxBytes = limitMb * 1024 * 1024;
    const minBytes = 1024; // 1KB

    if (file.size < minBytes) {
      Swal.fire({
        icon: "error",
        title: "File too small",
        text: "Your file is too small. Minimum allowed size is 1KB.",
        confirmButtonColor: "#38915c",
      });
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (file.size > maxBytes) {
      Swal.fire({
        icon: "error",
        title: "File too large",
        text: `Your file is too large. The live server allows up to ${limitMb}MB. Please use a link for larger files.`,
        confirmButtonColor: "#38915c",
      });
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (uploadEndpoint) {
      setIsUploading(true);
      setUploadProgress(0);
      const form = new FormData();
      form.append(type === "video" ? "video" : "image", file);

      axios.post(uploadEndpoint, form, {
        headers: {
          Authorization: `Bearer ${getStoredToken() || ""}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 0, 
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
        }
      })
      .then((res) => {
        const data = res.data;
        onChange(data.url || "", data.fileName || file.name);
        setIsUploading(false);
        setUploadProgress(0);
      })
      .catch((err) => {
        setIsUploading(false);
        setUploadProgress(0);
        const message = err.response?.data?.message || err.message || `${type.charAt(0).toUpperCase() + type.slice(1)} upload failed`;
        Swal.fire({
          icon: "error",
          title: "Upload failed",
          text: message,
          confirmButtonColor: "#38915c",
        });
        if (inputRef.current) inputRef.current.value = "";
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      onChange(reader.result, file.name);
    };
    reader.readAsDataURL(file);
  };

  const clearValue = () => {
    onChange("", "");
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <FormControl fullWidth error={Boolean(error)} {...rest}>
      {label && (
        <Typography
          component="label"
          variant="body2"
          fontWeight={600}
          sx={{
            display: "block",
            mb: 1,
            color: error ? "error.main" : "text.secondary",
            transition: "color 0.2s ease-in-out",
          }}
        >
          {label} (URL or upload)
          {required && (
            <Box component="span" sx={{ color: "error.main", ml: 0.5 }}>
              *
            </Box>
          )}
        </Typography>
      )}
      <div className="space-y-2">
        <OutlinedInput
          id={inputId}
          placeholder={placeholder}
          value={isUploaded ? "" : value || ""}
          onChange={handleUrlChange}
          onBlur={onBlur}
          error={Boolean(error)}
          size="small"
          sx={{ borderRadius: "12px" }}
          fullWidth
        />
        <div className="flex flex-wrap items-center gap-2">
          <label className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 border-primary-200 bg-primary-50 text-primary-700 text-sm font-medium cursor-pointer hover:bg-primary-100">
            <span>Choose file</span>
            <input
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={handleFileChange}
              className="hidden"
            />
          </label>
          {isUploaded && (
            <span className="text-sm text-primary-600">
              ✓ Uploaded{fileName ? `: ${fileName}` : ""}
            </span>
          )}
          {value && (
            <button
              type="button"
              onClick={clearValue}
              className="text-sm text-gray-500 hover:text-danger-600"
            >
              Clear
            </button>
          )}
        </div>

        {isUploading && (
          <Box sx={{ width: '100%', mt: 1 }}>
            <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
              Uploading... {uploadProgress}%
            </Typography>
          </Box>
        )}

        {type === "image" && value && (
          <div className="mt-2 relative inline-block">
            <img
              src={value}
              alt="Preview"
              className="h-24 w-auto rounded-lg border border-gray-200 object-cover shadow-sm"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>
        )}
        <p className="text-xs text-gray-500">
          {type === "image"
            ? "Image: paste link or upload file."
            : `Video: paste link or upload file (max ${MAX_VIDEO_SIZE_MB}MB).`}
        </p>
      </div>
      {error && <FormHelperText sx={{ ml: 0 }}>{error}</FormHelperText>}
    </FormControl>
  );
};

export default Upload;
